import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Zap, Plus, Edit2, Trash2, Play, Pause, Mail,
  XCircle, Clock, Settings
} from 'lucide-react';

import { API_BASE_URL, getAuthHeaders as getCoreAuthHeaders } from '../../api/core.js';
import { isFlagOn } from '../../utils/dbFlags';

function getAuthHeaders() {
  return { 'Content-Type': 'application/json', ...getCoreAuthHeaders() };
}

// API functions with authentication
const workflowTriggerAPI = {
  getAll: async () => {
    const res = await fetch(`${API_BASE_URL}/workflow_email_triggers`, {
      headers: getAuthHeaders()
    });
    return res.json();
  },
  create: async (data) => {
    const res = await fetch(`${API_BASE_URL}/workflow_email_triggers`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return res.json();
  },
  update: async (id, data) => {
    const res = await fetch(`${API_BASE_URL}/workflow_email_triggers/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return res.json();
  },
  delete: async (id) => {
    const res = await fetch(`${API_BASE_URL}/workflow_email_triggers/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return res.json();
  }
};

const emailTemplateAPI = {
  getAll: async () => {
    const res = await fetch(`${API_BASE_URL}/email_templates`, {
      headers: getAuthHeaders()
    });
    return res.json();
  }
};

// Workflow definitions — labels are resolved at render time via t(), never baked in here
// (a module-level constant is evaluated once, before any language switch).
const WORKFLOWS = {
  claim_lifecycle: {
    nameKey: 'emails.triggers.workflowNames.claim_lifecycle',
    statusNs: 'claims.status',
    statuses: ['draft', 'declared', 'acknowledged', 'expert_pending', 'expert_done', 'repair_authorized', 'indemnified', 'closed', 'rejected']
  },
  maintenance_lifecycle: {
    nameKey: 'emails.triggers.workflowNames.maintenance_lifecycle',
    statusNs: 'maintenance.status',
    statuses: ['draft', 'submitted', 'approved', 'scheduled', 'in_progress', 'completed', 'cancelled']
  },
  insurance_contract_lifecycle: {
    nameKey: 'emails.triggers.workflowNames.insurance_contract_lifecycle',
    statusNs: 'emails.triggers.workflows.insurance.statuses',
    statuses: ['quote_requested', 'quote_received', 'negotiating', 'bound', 'active', 'suspended', 'cancelled', 'expired']
  }
};

const RECIPIENT_TYPES = ['driver', 'manager', 'approver', 'broker', 'vendor', 'custom'];

function WorkflowTriggersTab({ onMessage }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showEditor, setShowEditor] = useState(false);
  const [editingTrigger, setEditingTrigger] = useState(null);
  const [filterWorkflow, setFilterWorkflow] = useState('all');

  // Fetch triggers and templates
  const { data: triggersData, isLoading: loadingTriggers } = useQuery({
    queryKey: ['workflowTriggers'],
    queryFn: workflowTriggerAPI.getAll
  });

  const { data: templatesData } = useQuery({
    queryKey: ['emailTemplates'],
    queryFn: emailTemplateAPI.getAll
  });

  const triggers = triggersData?.data || [];
  const templates = templatesData?.data || [];

  // Mutations
  const createMutation = useMutation({
    mutationFn: workflowTriggerAPI.create,
    onSuccess: () => {
      queryClient.invalidateQueries(['workflowTriggers']);
      setShowEditor(false);
      setEditingTrigger(null);
      onMessage?.({ title: t('common.messages.successTitle'), message: t('emails.triggers.toasts.created'), type: 'success' });
    },
    onError: (err) => {
      onMessage?.({ title: t('common.messages.errorTitle'), message: err.message, type: 'error' });
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => workflowTriggerAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['workflowTriggers']);
      setShowEditor(false);
      setEditingTrigger(null);
      onMessage?.({ title: t('common.messages.successTitle'), message: t('emails.triggers.toasts.updated'), type: 'success' });
    },
    onError: (err) => {
      onMessage?.({ title: t('common.messages.errorTitle'), message: err.message, type: 'error' });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: workflowTriggerAPI.delete,
    onSuccess: () => {
      queryClient.invalidateQueries(['workflowTriggers']);
      onMessage?.({ title: t('common.messages.successTitle'), message: t('emails.triggers.toasts.deleted'), type: 'success' });
    }
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, is_active }) => workflowTriggerAPI.update(id, { is_active }),
    onSuccess: () => {
      queryClient.invalidateQueries(['workflowTriggers']);
    }
  });

  // Filter triggers
  const filteredTriggers = filterWorkflow === 'all'
    ? triggers
    : triggers.filter(t => t.workflow_name === filterWorkflow);

  // Group triggers by workflow
  const groupedTriggers = filteredTriggers.reduce((acc, trigger) => {
    const workflow = trigger.workflow_name;
    if (!acc[workflow]) acc[workflow] = [];
    acc[workflow].push(trigger);
    return acc;
  }, {});

  const handleSave = (formData) => {
    if (editingTrigger) {
      updateMutation.mutate({ id: editingTrigger.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  if (loadingTriggers) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-500" />
            {t('emails.triggers.title')}
          </h2>
          <select
            value={filterWorkflow}
            onChange={(e) => setFilterWorkflow(e.target.value)}
            className="text-sm border rounded-lg px-3 py-1.5"
          >
            <option value="all">{t('emails.triggers.filterAll')}</option>
            {Object.entries(WORKFLOWS).map(([key, wf]) => (
              <option key={key} value={key}>{t(wf.nameKey)}</option>
            ))}
          </select>
        </div>
        <button
          onClick={() => {
            setEditingTrigger(null);
            setShowEditor(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          {t('emails.triggers.new')}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border p-4">
          <div className="text-2xl font-bold text-blue-600">{triggers.length}</div>
          <div className="text-sm text-gray-500">{t('emails.triggers.stats.total')}</div>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <div className="text-2xl font-bold text-green-600">
            {triggers.filter(tr => isFlagOn(tr.is_active)).length}
          </div>
          <div className="text-sm text-gray-500">{t('emails.triggers.stats.active')}</div>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <div className="text-2xl font-bold text-yellow-600">
            {triggers.filter(tr => tr.send_automatically === 1).length}
          </div>
          <div className="text-sm text-gray-500">{t('emails.triggers.stats.autoSend')}</div>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <div className="text-2xl font-bold text-purple-600">
            {Object.keys(groupedTriggers).length}
          </div>
          <div className="text-sm text-gray-500">{t('emails.triggers.stats.configuredWorkflows')}</div>
        </div>
      </div>

      {/* Triggers list grouped by workflow */}
      {Object.entries(groupedTriggers).length === 0 ? (
        <div className="bg-white rounded-lg border p-8 text-center">
          <Zap className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">{t('emails.triggers.empty.title')}</p>
          <p className="text-sm text-gray-400 mt-1">
            {t('emails.triggers.empty.description')}
          </p>
        </div>
      ) : (
        Object.entries(groupedTriggers).map(([workflowName, workflowTriggers]) => (
          <div key={workflowName} className="bg-white rounded-lg border">
            <div className="px-4 py-3 border-b bg-gray-50 flex items-center gap-2">
              <Settings className="w-4 h-4 text-gray-500" />
              <span className="font-medium">{WORKFLOWS[workflowName] ? t(WORKFLOWS[workflowName].nameKey) : workflowName}</span>
              <span className="text-sm text-gray-500">{t('emails.triggers.card.count', { count: workflowTriggers.length })}</span>
            </div>
            <div className="divide-y">
              {workflowTriggers.map(trigger => (
                <div key={trigger.id} className="p-4 hover:bg-gray-50 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {/* Status indicator */}
                    <div className={`w-2 h-2 rounded-full ${isFlagOn(trigger.is_active) ? 'bg-green-500' : 'bg-gray-300'}`} />

                    {/* Trigger info */}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{trigger.template_name}</span>
                        <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                          {WORKFLOWS[workflowName]?.statuses.includes(trigger.trigger_status)
                            ? t(`${WORKFLOWS[workflowName].statusNs}.${trigger.trigger_status}`)
                            : trigger.trigger_status}
                        </span>
                        {trigger.send_automatically === 1 && (
                          <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded flex items-center gap-1">
                            <Zap className="w-3 h-3" />
                            {t('emails.triggers.badge.auto')}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500 flex items-center gap-3 mt-1">
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {t(`emails.triggers.recipientTypes.${trigger.recipient_type}`, { defaultValue: trigger.recipient_type })}
                          {trigger.recipient_type === 'custom' && trigger.recipient_email && (
                            <span className="text-gray-400">({trigger.recipient_email})</span>
                          )}
                        </span>
                        {trigger.delay_minutes > 0 && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {t('emails.triggers.delay', { minutes: trigger.delay_minutes })}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggleActiveMutation.mutate({
                        id: trigger.id,
                        is_active: isFlagOn(trigger.is_active) ? 0 : 1
                      })}
                      className={`p-2 rounded-lg ${isFlagOn(trigger.is_active) ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'}`}
                      title={isFlagOn(trigger.is_active) ? t('emails.triggers.actions.deactivate') : t('emails.triggers.actions.activate')}
                    >
                      {isFlagOn(trigger.is_active) ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => {
                        setEditingTrigger(trigger);
                        setShowEditor(true);
                      }}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                      title={t('common.actions.edit')}
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm(t('emails.triggers.confirmDelete'))) {
                          deleteMutation.mutate(trigger.id);
                        }
                      }}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                      title={t('common.actions.delete')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      {/* Editor Modal */}
      {showEditor && (
        <TriggerEditorModal
          trigger={editingTrigger}
          templates={templates}
          onClose={() => {
            setShowEditor(false);
            setEditingTrigger(null);
          }}
          onSave={handleSave}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />
      )}
    </div>
  );
}

// Trigger Editor Modal
function TriggerEditorModal({ trigger, templates, onClose, onSave, isLoading }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    workflow_name: trigger?.workflow_name || 'claim_lifecycle',
    trigger_status: trigger?.trigger_status || '',
    template_id: trigger?.template_id || '',
    recipient_type: trigger?.recipient_type || 'driver',
    recipient_email: trigger?.recipient_email || '',
    delay_minutes: trigger?.delay_minutes || 0,
    send_automatically: trigger?.send_automatically === 1,
    is_active: trigger?.is_active !== 0
  });

  const selectedWorkflow = WORKFLOWS[formData.workflow_name];

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      template_id: parseInt(formData.template_id),
      send_automatically: formData.send_automatically ? 1 : 0,
      is_active: formData.is_active ? 1 : 0
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg m-4">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h3 className="font-semibold">
            {trigger ? t('emails.triggers.modal.editTitle') : t('emails.triggers.modal.newTitle')}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XCircle className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Workflow */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('emails.triggers.modal.workflow')}</label>
            <select
              value={formData.workflow_name}
              onChange={(e) => setFormData({ ...formData, workflow_name: e.target.value, trigger_status: '' })}
              className="w-full border rounded-lg px-3 py-2"
              required
            >
              {Object.entries(WORKFLOWS).map(([key, wf]) => (
                <option key={key} value={key}>{t(wf.nameKey)}</option>
              ))}
            </select>
          </div>

          {/* Trigger Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('emails.triggers.modal.triggerStatus')}</label>
            <select
              value={formData.trigger_status}
              onChange={(e) => setFormData({ ...formData, trigger_status: e.target.value })}
              className="w-full border rounded-lg px-3 py-2"
              required
            >
              <option value="">{t('emails.triggers.modal.selectStatus')}</option>
              {selectedWorkflow?.statuses.map(statusCode => (
                <option key={statusCode} value={statusCode}>{t(`${selectedWorkflow.statusNs}.${statusCode}`)}</option>
              ))}
            </select>
          </div>

          {/* Template */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('emails.triggers.modal.template')}</label>
            <select
              value={formData.template_id}
              onChange={(e) => setFormData({ ...formData, template_id: e.target.value })}
              className="w-full border rounded-lg px-3 py-2"
              required
            >
              <option value="">{t('emails.triggers.modal.selectTemplate')}</option>
              {templates.map(template => (
                <option key={template.id} value={template.id}>
                  {template.template_name} ({template.category})
                </option>
              ))}
            </select>
          </div>

          {/* Recipient Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('emails.triggers.modal.recipient')}</label>
            <select
              value={formData.recipient_type}
              onChange={(e) => setFormData({ ...formData, recipient_type: e.target.value })}
              className="w-full border rounded-lg px-3 py-2"
              required
            >
              {RECIPIENT_TYPES.map(code => (
                <option key={code} value={code}>{t(`emails.triggers.recipientTypes.${code}`)}</option>
              ))}
            </select>
          </div>

          {/* Custom Email */}
          {formData.recipient_type === 'custom' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('emails.triggers.recipientTypes.custom')}</label>
              <input
                type="email"
                value={formData.recipient_email}
                onChange={(e) => setFormData({ ...formData, recipient_email: e.target.value })}
                className="w-full border rounded-lg px-3 py-2"
                placeholder="email@exemple.com"
                required
              />
            </div>
          )}

          {/* Delay */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('emails.triggers.modal.delay')}</label>
            <input
              type="number"
              value={formData.delay_minutes}
              onChange={(e) => setFormData({ ...formData, delay_minutes: parseInt(e.target.value) || 0 })}
              className="w-full border rounded-lg px-3 py-2"
              min="0"
            />
            <p className="text-xs text-gray-500 mt-1">{t('emails.triggers.modal.delayHint')}</p>
          </div>

          {/* Options */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.send_automatically}
                onChange={(e) => setFormData({ ...formData, send_automatically: e.target.checked })}
                className="rounded border-gray-300 text-blue-600"
              />
              <span className="text-sm">{t('emails.triggers.modal.autoSend')}</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="rounded border-gray-300 text-blue-600"
              />
              <span className="text-sm">{t('emails.triggers.modal.active')}</span>
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              {t('common.actions.cancel')}
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? t('common.states.saving') : t('common.actions.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default WorkflowTriggersTab;
